"use client";

import { useState } from "react";
import Image from "next/image";
import {
  Globe,
  Loader2,
  CheckCircle2,
  AlertCircle,
  XCircle,
} from "lucide-react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface MetaPayload {
  title: string | null;
  description: string | null;
  canonicalUrl: string | null;
  favicon: string | null;
  ogTitle: string | null;
  ogDescription: string | null;
  ogImage: string | null;
  ogUrl: string | null;
  ogType: string | null;
  twitterCard: string | null;
  twitterTitle: string | null;
  twitterDescription: string | null;
  twitterImage: string | null;
}

interface OgPreviewResult {
  url: string;
  fetchedAt: string;
  meta: MetaPayload;
}

const normalizeInputUrl = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const ensured = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  try {
    const url = new URL(ensured);
    if (url.protocol === "http:") {
      url.protocol = "https:";
    }
    url.hash = "";
    return url.toString();
  } catch {
    return null;
  }
};

const faviconFromUrl = (value: string): string | null => {
  const normalized = normalizeInputUrl(value);
  if (!normalized) return null;
  try {
    const url = new URL(normalized);
    return `https://www.google.com/s2/favicons?domain=${url.hostname}&sz=64`;
  } catch {
    return null;
  }
};

export function OgPreviewTool() {
  const [url, setUrl] = useState("");
  const [favicon, setFavicon] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OgPreviewResult | null>(null);

  const updateFavicon = (value: string) => {
    const next = faviconFromUrl(value);
    setFavicon(next);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!url.trim()) {
      setError("Please enter a valid URL.");
      return;
    }

    const normalized = normalizeInputUrl(url);
    if (!normalized) {
      setError("Please enter a valid URL.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/tools/og-preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: normalized }),
      });
      const json = await response.json();

      if (!response.ok || !json.success) {
        throw new Error(json.error ?? "Unable to fetch preview.");
      }

      setResult(json.data as OgPreviewResult);
    } catch (err) {
      setResult(null);
      setError(
        err instanceof Error ? err.message : "Something went wrong. Try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <form
        onSubmit={handleSubmit}
        className="flex w-full max-w-2xl flex-col gap-3 sm:mx-auto sm:flex-row sm:items-center sm:gap-4"
      >
        <div className="relative flex-1">
          <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2">
            {favicon ? (
              <Image
                src={favicon}
                alt=""
                width={20}
                height={20}
                unoptimized
                className="h-5 w-5 sm:h-6 sm:w-6 rounded-sm"
              />
            ) : (
              <Globe className="h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground" />
            )}
          </div>
          <span className="pointer-events-none absolute left-12 sm:left-14 top-1/2 h-6 sm:h-8 -translate-y-1/2 border-l border-border" />
          <Input
            type="text"
            placeholder="website.com"
            className={cn(
              "h-10 w-full pl-14 text-sm sm:h-12 sm:pl-16 sm:text-base border-2 border-border/20 bg-transparent focus:border-primary/50",
              error && "border-destructive"
            )}
            value={url}
            onChange={(event) => {
              setUrl(event.target.value);
              updateFavicon(event.target.value);
              setError(null);
            }}
            autoComplete="url"
            aria-label="Website URL"
          />
        </div>
        <Button
          type="submit"
          size="lg"
          disabled={isLoading}
          className="shrink-0 h-10 w-full sm:h-12 sm:w-auto"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading
            </>
          ) : (
            "Preview"
          )}
        </Button>
      </form>
      {error && (
        <p className="mt-4 text-center text-sm font-medium text-destructive">
          {error}
        </p>
      )}

      {result ? (
        <div className="space-y-8">
          {/* Social Previews - Main Focus - Top Priority */}
          <div className="grid gap-6 lg:grid-cols-2">
            <SocialPreview
              variant="Open Graph"
              title={
                result.meta.ogTitle ?? result.meta.title ?? "No title found"
              }
              description={
                result.meta.ogDescription ??
                result.meta.description ??
                "This page does not have an Open Graph description yet."
              }
              url={result.meta.ogUrl ?? result.url}
              imageUrl={result.meta.ogImage}
            />
            <SocialPreview
              variant="Twitter"
              title={
                result.meta.twitterTitle ??
                result.meta.title ??
                "No title found"
              }
              description={
                result.meta.twitterDescription ??
                result.meta.description ??
                "Add twitter:description to control how this appears on X."
              }
              url={result.url}
              imageUrl={result.meta.twitterImage ?? result.meta.ogImage}
              cardType={result.meta.twitterCard}
            />
          </div>

          {/* Open Graph Tags */}
          <div className="rounded-xl border border-border/60 bg-card/40 p-6 sm:p-8">
            <h3 className="mb-6 text-lg font-semibold tracking-tight">
              Open Graph Tags
            </h3>
            <div className="space-y-3">
              <MetaRow
                label="og:title"
                value={result.meta.ogTitle}
                fallback="Missing"
                detail={
                  result.meta.ogTitle
                    ? `${result.meta.ogTitle.length} chars`
                    : "Required for social sharing"
                }
                status={
                  result.meta.ogTitle
                    ? result.meta.ogTitle.length >= 30 &&
                      result.meta.ogTitle.length <= 60
                      ? "pass"
                      : "warning"
                    : "fail"
                }
              />
              <MetaRow
                label="og:description"
                value={result.meta.ogDescription}
                fallback="Missing"
                detail={
                  result.meta.ogDescription
                    ? `${result.meta.ogDescription.length} chars`
                    : "Recommended for better previews"
                }
                status={
                  result.meta.ogDescription
                    ? result.meta.ogDescription.length >= 120 &&
                      result.meta.ogDescription.length <= 160
                      ? "pass"
                      : "warning"
                    : "warning"
                }
              />
              <MetaRow
                label="og:image"
                value={result.meta.ogImage ? "Present" : null}
                fallback="Missing"
                detail="1200×630px recommended"
                status={result.meta.ogImage ? "pass" : "fail"}
              />
              <MetaRow
                label="og:url"
                value={result.meta.ogUrl}
                fallback="Missing"
                detail="Canonical URL for sharing"
                status={result.meta.ogUrl ? "pass" : "warning"}
              />
              <MetaRow
                label="og:type"
                value={result.meta.ogType ?? "website"}
                fallback="website (default)"
                detail="Content type"
                status="pass"
              />
            </div>
          </div>

          {/* Twitter Tags */}
          <div className="rounded-xl border border-border/60 bg-card/40 p-6 sm:p-8">
            <h3 className="mb-6 text-lg font-semibold tracking-tight">
              Twitter Card Tags
            </h3>
            <div className="space-y-3">
              <MetaRow
                label="twitter:card"
                value={result.meta.twitterCard ?? "summary"}
                fallback="summary (default)"
                detail="Card type (summary, summary_large_image)"
                status={result.meta.twitterCard ? "pass" : "warning"}
              />
              <MetaRow
                label="twitter:title"
                value={result.meta.twitterTitle}
                fallback="Missing"
                detail={
                  result.meta.twitterTitle
                    ? `${result.meta.twitterTitle.length} chars`
                    : "Falls back to og:title"
                }
                status={result.meta.twitterTitle ? "pass" : "warning"}
              />
              <MetaRow
                label="twitter:description"
                value={result.meta.twitterDescription}
                fallback="Missing"
                detail={
                  result.meta.twitterDescription
                    ? `${result.meta.twitterDescription.length} chars`
                    : "Falls back to og:description"
                }
                status={result.meta.twitterDescription ? "pass" : "warning"}
              />
              <MetaRow
                label="twitter:image"
                value={result.meta.twitterImage ? "Present" : null}
                fallback="Missing"
                detail="Falls back to og:image"
                status={result.meta.twitterImage ? "pass" : "warning"}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-border/60 bg-muted/20 p-12 text-center">
          <p className="text-sm text-muted-foreground">
            Enter a URL above to preview how it will appear when shared on
            social media.
          </p>
        </div>
      )}
    </div>
  );
}

function MetaRow({
  label,
  value,
  fallback,
  detail,
  status,
}: {
  label: string;
  value: string | null;
  fallback: string;
  detail: string;
  status?: "pass" | "warning" | "fail";
}) {
  return (
    <div
      className={cn(
        "rounded-lg border p-4 transition-colors",
        status === "pass"
          ? "border-emerald-500/30 bg-emerald-500/5"
          : status === "warning"
          ? "border-amber-500/30 bg-amber-500/5"
          : status === "fail"
          ? "border-rose-500/30 bg-rose-500/5"
          : "border-border/60 bg-muted/30"
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
          {label}
        </p>
        {status && (
          <div className="flex-shrink-0">
            {status === "pass" ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-500" />
            ) : status === "warning" ? (
              <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-500" />
            ) : (
              <XCircle className="h-4 w-4 text-rose-600 dark:text-rose-500" />
            )}
          </div>
        )}
      </div>
      <p className="mt-2 text-sm font-semibold text-foreground line-clamp-2">
        {value ?? fallback}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">{detail}</p>
    </div>
  );
}

function SocialPreview({
  variant,
  title,
  description,
  url,
  imageUrl,
  cardType,
}: {
  variant: "Open Graph" | "Twitter";
  title: string;
  description: string;
  url: string;
  imageUrl: string | null;
  cardType?: string | null;
}) {
  return (
    <div className="group flex flex-col overflow-hidden rounded-xl border border-border/60 bg-card/40 shadow-sm transition-all hover:border-border hover:bg-card/60 hover:shadow-lg">
      <div className="aspect-[1200/630] w-full overflow-hidden bg-muted/40">
        {imageUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={imageUrl}
            alt={`${variant} cover`}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center border-b border-border/60 bg-muted/20">
            <p className="text-sm text-muted-foreground">No {variant} image</p>
          </div>
        )}
      </div>
      <div className="space-y-2.5 p-5">
        <div className="flex items-center gap-2">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            {variant}
          </p>
          {variant === "Twitter" && cardType && (
            <>
              <span className="text-muted-foreground/50">·</span>
              <span className="text-xs font-medium text-muted-foreground">
                {cardType.toUpperCase()}
              </span>
            </>
          )}
        </div>
        <h4 className="text-base font-semibold leading-tight text-foreground line-clamp-2">
          {title}
        </h4>
        <p className="text-sm leading-relaxed text-muted-foreground line-clamp-2">
          {description}
        </p>
        <p className="text-xs font-medium text-muted-foreground truncate">
          {url.replace(/^https?:\/\//, "")}
        </p>
      </div>
    </div>
  );
}
